import React from 'react';
import { FileText } from 'lucide-react';
import { formatCurrency, parseCurrency } from '../../utils/formatting';

const PROFIT_SPLIT_DOC_URL = 'https://drive.google.com/file/d/1_LhhzxGP59GsmWh6oPNrKEqHGe78GzTA/view?usp=sharing';

const SPLIT_AGREEMENTS = [
  {
    title: "I've reviewed the Profit Split Breakdown and agree to it",
    body: 'I have read the JV Partnership Profit Split Checklist and I agree to the 70/30, 50/50, and referral terms it lays out.',
  },
  {
    title: 'Declaring 50/50 intent now, at submission',
    body: 'I am declaring 50/50 intent now, in a note on this deal — before it is worked or approved. I understand that undeclared deals default to 70/30.',
  },
  {
    title: 'I still own every 70/30 responsibility',
    body: 'I understand 50/50 requires everything in the 70/30 tier plus the items below. Sourcing, homeowner follow-up, portal updates, and staying engaged through close are still on me.',
  },
  {
    title: 'Title — I run it independently',
    body: 'I will order and review the title report myself, work directly with the title company, and help resolve any title issues. I will not wait for NFE to start this.',
  },
  {
    title: 'Contracts — I own and renegotiate my side only',
    body: "I will secure a signed agreement on whichever side is mine (A-to-B or B-to-C) and handle renegotiations on that side. Each party owns their side — I don't work NFE's side, and NFE doesn't work mine.",
  },
  {
    title: 'Funding — earnest money and deal expenses are on me',
    body: 'If required, I will cover earnest money and any deal-related expenses on my side, like utilities, an early tax payoff, liens, or cleanouts. NFE does not front these costs at 50/50.',
  },
  {
    title: 'Documentation — proof of every dollar',
    body: 'I will keep receipts and records of every dollar I contribute. I understand no documentation means no credit in the final HUD distribution at closing.',
  },
  {
    title: 'I understand the split can revert to 70/30',
    body: 'If NFE has to carry the operational lift on any major part of this deal, I understand it reverts to a 70/30 split.',
  },
];

interface Step5Props {
  formData: any;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  onSplitAgreementChange: (index: number, checked: boolean) => void;
}

export function Step5SellerSituation({ formData, onChange, onSplitAgreementChange }: Step5Props) {
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow user to type, format on blur
    onChange(e);
  };

  const handleCurrencyBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const formatted = formatCurrency(parseCurrency(value));
      onChange({
        ...e,
        target: {
          ...e.target,
          name: e.target.name,
          value: formatted
        }
      } as any);
    }
  };
  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-tdb-primary mb-6 flex items-center space-x-2">
        <FileText className="w-5 h-5 text-tdb-accent" />
        <span>Seller Financial Situation</span>
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Employment Status *
          </label>
          <input
            type="text"
            name="employmentStatus"
            value={formData.employmentStatus}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
            placeholder="Employed, Self-Employed, Retired, etc."
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Total Monthly Income *
          </label>
          <input
            type="text"
            name="monthlyIncome"
            value={formData.monthlyIncome}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
            placeholder="$5,000"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Income Breakdown *
          </label>
          <p className="text-xs text-gray-500 mb-2">Briefly explain how you make your income</p>
          <textarea
            name="incomeBreakdown"
            value={formData.incomeBreakdown}
            onChange={onChange}
            required
            rows={3}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all resize-none"
            placeholder="e.g., Salary from full-time job, rental income, etc."
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Total Monthly Utility Expenses
          </label>
          <input
            type="text"
            name="utilityExpenses"
            value={formData.utilityExpenses}
            onChange={handleCurrencyChange}
            onBlur={handleCurrencyBlur}
            className="w-full px-4 py-3 border-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
            placeholder="$300"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Total Monthly Additional Expenses
          </label>
          <input
            type="text"
            name="additionalExpenses"
            value={formData.additionalExpenses}
            onChange={handleCurrencyChange}
            onBlur={handleCurrencyBlur}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
            placeholder="$500"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Additional Expenses Breakdown
          </label>
          <p className="text-xs text-gray-500 mb-2">Briefly explain your additional expenses</p>
          <textarea
            name="expensesBreakdown"
            value={formData.expensesBreakdown}
            onChange={onChange}
            rows={3}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all resize-none"
            placeholder="e.g., Car payment, credit cards, medical expenses..."
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Is the seller realistic about losing their home?
          </label>
          <textarea
            name="sellerRealistic"
            value={formData.sellerRealistic}
            onChange={onChange}
            rows={3}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all resize-none"
            placeholder="Describe the seller's understanding of their situation..."
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Does the seller want to stay in their home or have they moved on?
          </label>
          <select
            name="sellerWantsToStay"
            value={formData.sellerWantsToStay}
            onChange={onChange}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          >
            <option value="">Select...</option>
            <option value="yes">Yes - wants to stay</option>
            <option value="no">No - ready to move on</option>
            <option value="maybe">Maybe - unsure</option>
          </select>
        </div>

        {/* Deal Split Breakdown */}
        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Deal Split *
          </label>
          <select
            name="jvSplit"
            value={formData.jvSplit}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          >
            <option value="">Choose One...</option>
            <option value="70/30">70/30 — NFE leads, partner supports</option>
            <option value="50/50">50/50 — Equal partnership</option>
          </select>
          {formData.jvSplit === '70/30' && (
            <p className="text-xs text-gray-500 mt-1">
              70/30 split: NFE takes 70%, partner receives 30% of net proceeds.
            </p>
          )}
          {formData.jvSplit === '50/50' && (
            <div className="mt-4 space-y-3">
              <p className="text-sm font-semibold text-tdb-primary">
                50/50 Agreement Checklist — check each to confirm:
              </p>
              <p className="text-xs text-gray-500">
                Before checking, review the{' '}
                <a
                  href={PROFIT_SPLIT_DOC_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-tdb-accent hover:text-tdb-secondary font-semibold underline"
                >
                  JV Partnership Profit Split Checklist
                </a>
                .
              </p>
              {SPLIT_AGREEMENTS.map((agreement, index) => (
                <label key={index} className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.jvSplitAgreements?.[index] ?? false}
                    onChange={(e) => onSplitAgreementChange(index, e.target.checked)}
                    className="mt-1 w-4 h-4 text-tdb-accent rounded"
                  />
                  <div>
                    <p className="text-sm font-semibold text-tdb-primary">{agreement.title}</p>
                    <p className="text-xs text-gray-500">{agreement.body}</p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

      </div>

      {/* Submission Summary */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-xl border-2 border-blue-200 mt-6">
        <h4 className="font-bold text-tdb-primary mb-4 flex items-center space-x-2">
          <FileText className="w-5 h-5" />
          <span>Submission Summary</span>
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="font-semibold">Partner:</span> {formData.partnerFirstName} {formData.partnerLastName}
          </div>
          <div>
            <span className="font-semibold">Property:</span> {formData.propertyStreet}, {formData.propertyCity}, {formData.propertyState}
          </div>
          <div>
            <span className="font-semibold">Deal Type:</span> {formData.dealType}
          </div>
          <div>
            <span className="font-semibold">Photos:</span> {formData.propertyPhotos.length} uploaded
          </div>
          <div>
            <span className="font-semibold">MAO Reports:</span> {formData.maoReports.length} uploaded
          </div>
          <div>
            <span className="font-semibold">Documents:</span> {formData.additionalDocs.length} uploaded
          </div>
          <div>
            <span className="font-semibold">Suggested Offer:</span> {formData.suggestedOffer}
          </div>
          <div>
            <span className="font-semibold">Deal Split:</span> {formData.jvSplit || '—'}
          </div>
        </div>
      </div>
    </div>
  );
}
